// React hook that maintains a single WebSocket connection per session and exposes
// resolved feature-flags reactively. Used by the agent Workspace + Layout.
//
// Backend sends:
//   { type: "hello",                  flags, user_id, tenant_id }
//   { type: "feature_flags.role_update", config: { roles: {...} } }
//   { type: "feature_flags.user_override", user_id, overrides }
//
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";

const RAW_BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const WS_BASE = RAW_BACKEND.replace(/^http/, "ws");

export function useFeatureFlags() {
  const { user } = useAuth();
  const [flags, setFlags] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("c7_token");
    if (!token) return;

    let stopped = false;
    let pingTimer = null;
    let reconnectTimer = null;

    function connect() {
      if (stopped) return;
      const url = `${WS_BASE}/api/ws/agent?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        pingTimer = setInterval(() => { try { ws.send("ping"); } catch {} }, 25_000);
      };
      ws.onmessage = (e) => {
        if (e.data === "pong") return;
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "hello" && msg.flags) setFlags(msg.flags);
          else if (msg.type === "feature_flags.role_update") {
            // Refetch resolved flags via REST (handles per-user overrides)
            fetch(`${RAW_BACKEND}/api/me/feature-flags`, { headers: { Authorization: `Bearer ${token}` } })
              .then((r) => r.json()).then((d) => setFlags(d)).catch(() => {});
          }
          else if (msg.type === "feature_flags.user_override" && msg.user_id === user.id) {
            // Apply overrides on top of current map
            setFlags((cur) => ({ ...(cur || {}), ...(msg.overrides || {}) }));
          }
        } catch {}
      };
      ws.onclose = () => {
        setConnected(false);
        if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
        if (!stopped) reconnectTimer = setTimeout(connect, 3_000);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    }
    // Also fetch once at startup (covers cases where WS is blocked)
    fetch(`${RAW_BACKEND}/api/me/feature-flags`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then(setFlags).catch(() => {});
    connect();
    return () => {
      stopped = true;
      if (pingTimer) clearInterval(pingTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch {}
    };
  }, [user]);

  return { flags, connected };
}
